/**
 * Builds the HTML body for a credit note notification email.
 *
 * @param {object} cn  - Credit note data from the API createFromBookings response.
 *   Expected fields: creditNoteNumber, talentName, agencyName, agencyPercentage,
 *                    netAmount, vatAmount, grossAmount, bookingDates, invoiceDate,
 *                    invoiceType, createdBy
 * @returns {string} HTML string
 */
export function buildCreditNoteEmailHtml(cn) {
  const number      = cn.creditNoteNumber  || cn.credit_note_number  || '—';
  const talentName  = cn.talentName        || cn.talent_name         || '—';
  const agencyName  = cn.agencyName        || cn.agency_name         || null;
  const agencyPct   = cn.agencyPercentage  ?? cn.agency_percentage   ?? null;
  const net         = formatMoney(cn.netAmount   ?? cn.net_amount);
  const vat         = formatMoney(cn.vatAmount   ?? cn.vat_amount);
  const gross       = formatMoney(cn.grossAmount ?? cn.gross_amount);
  const dates       = cn.bookingDates      || cn.booking_dates       || cn.invoiceDate || cn.invoice_date || '—';
  const type        = cn.invoiceType       || cn.invoice_type        || '—';

  const agencyRow = agencyName && agencyName.trim().length > 1
    ? `<tr>
        <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;">Agency</td>
        <td style="padding:6px 0;font-weight:600;">${agencyName}${agencyPct != null ? ` &nbsp;(${Number(agencyPct)}%)` : ''}</td>
       </tr>`
    : '';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
</head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,Helvetica,sans-serif;color:#121212;">

  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;max-width:600px;width:100%;">

          <!-- Header -->
          <tr>
            <td style="background:#121212;padding:28px 36px;">
              <p style="margin:0;color:#ffffff;font-size:22px;font-weight:800;letter-spacing:0.04em;text-transform:uppercase;">
                Set-Cast
              </p>
              <p style="margin:6px 0 0;color:#aaaaaa;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;">
                Credit Note Notification
              </p>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:36px 36px 28px;">
              <p style="margin:0 0 20px;font-size:15px;line-height:1.6;">
                Dear ${talentName},
              </p>
              <p style="margin:0 0 24px;font-size:15px;line-height:1.6;">
                A credit note has been issued for you. Please find the PDF (Gutschrift) and
                XRechnung XML attached to this email for your records.
              </p>

              <!-- Credit note summary -->
              <table cellpadding="0" cellspacing="0" style="width:100%;border-top:2px solid #121212;margin-bottom:28px;">
                <tr>
                  <td colspan="2" style="padding:12px 0 8px;">
                    <span style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.08em;color:#666;">
                      Credit Note Details
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;">Credit note no.</td>
                  <td style="padding:6px 0;font-weight:700;font-size:15px;">${number}</td>
                </tr>
                <tr>
                  <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;">Type</td>
                  <td style="padding:6px 0;font-weight:600;">${type}</td>
                </tr>
                <tr>
                  <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;">Talent</td>
                  <td style="padding:6px 0;font-weight:600;">${talentName}</td>
                </tr>
                ${agencyRow}
                <tr>
                  <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;">Working date(s)</td>
                  <td style="padding:6px 0;">${String(dates).replace(/,/g, '<br>')}</td>
                </tr>
              </table>

              <!-- Amount summary -->
              <table cellpadding="0" cellspacing="0" style="width:100%;border-top:2px solid #121212;margin-bottom:28px;">
                <tr>
                  <td colspan="2" style="padding:12px 0 8px;">
                    <span style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.08em;color:#666;">
                      Amount
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style="padding:5px 12px 5px 0;color:#666;">Net</td>
                  <td style="padding:5px 0;text-align:right;">${net} €</td>
                </tr>
                <tr>
                  <td style="padding:5px 12px 5px 0;color:#666;">VAT</td>
                  <td style="padding:5px 0;text-align:right;">${vat} €</td>
                </tr>
                <tr style="border-top:1px solid #eee;">
                  <td style="padding:10px 12px 6px 0;font-weight:800;font-size:15px;">Gross total</td>
                  <td style="padding:10px 0 6px;text-align:right;font-weight:800;font-size:15px;">${gross} €</td>
                </tr>
              </table>

              <p style="margin:0 0 12px;font-size:14px;line-height:1.6;color:#444;">
                If you have any questions about this credit note, please contact us at
                <a href="mailto:talents@set-cast.com" style="color:#121212;font-weight:700;">talents@set-cast.com</a>.
              </p>
              <p style="margin:0;font-size:14px;line-height:1.6;color:#444;">
                Thank you for working with us.
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background:#f4f4f4;padding:20px 36px;border-top:1px solid #e0e0e0;">
              <p style="margin:0;font-size:12px;color:#888;line-height:1.6;">
                <strong style="color:#444;">Set Cast CTA GmbH</strong><br>
                Essener Bogen 5 &nbsp;·&nbsp; 22419 Hamburg<br>
                <a href="mailto:talents@set-cast.com" style="color:#888;">talents@set-cast.com</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>

</body>
</html>
  `.trim();
}

function formatMoney(n) {
  if (n == null || n === '') return '—';
  const num = typeof n === 'number' ? n : parseFloat(n);
  return isNaN(num) ? String(n) : num.toFixed(2);
}
